import SSRIcon from '@ingka/ssr-icon';
import checkmarkPath from '@ingka/ssr-icon/paths/checkmark';
import { useLocation, useNavigate } from 'react-router';
import { useApp } from '@/context/AppContext';
import { useEntityName, useProductName } from '@/hooks/useCatalogNames';
import styles from './ProgressStepper.module.css';

type StepId = 'product' | 'entity' | 'classification' | 'file' | 'success';

const STEPS: { id: StepId; label: string; path: string }[] = [
  { id: 'product', label: 'Product', path: '/upload/product' },
  { id: 'entity', label: 'Entity', path: '/upload/entity' },
  { id: 'classification', label: 'Classification', path: '/upload/classification' },
  { id: 'file', label: 'Upload', path: '/upload/file' },
  { id: 'success', label: 'Done', path: '/upload/success' },
];

const STEP_ORDER: Record<StepId, number> = {
  product: 1,
  entity: 2,
  classification: 3,
  file: 4,
  success: 5,
};

export default function ProgressStepper() {
  const { product, entity, setEntity } = useApp();
  const productLabel = useProductName(product);
  const entityLabel = useEntityName(product, entity);
  const navigate = useNavigate();
  const location = useLocation();

  // Determine current step from URL
  const currentStepId = (location.pathname.split('/').pop() ?? 'product') as StepId;
  const current = STEP_ORDER[currentStepId] ?? 1;

  const jumpTo = (target: StepId) => {
    if (target === 'product') {
      setEntity(null);
      navigate('/upload/product');
    } else if (target === 'entity') {
      navigate('/upload/entity');
    }
  };

  return (
    <div className={styles.stepper}>
      <div className={styles.inner}>
        {STEPS.map((step, i) => {
          const stepNum = i + 1;
          const isDone = current > stepNum;
          const isActive = current === stepNum;
          const canJump = isDone && (step.id === 'product' || step.id === 'entity');

          let subLabel = '';
          if (step.id === 'product' && product) subLabel = productLabel;
          if (step.id === 'entity' && product && entity) subLabel = entityLabel;

          return (
            <div key={step.id} className={styles.item}>
              <button
                type="button"
                onClick={canJump ? () => jumpTo(step.id) : undefined}
                disabled={!canJump && !isActive}
                className={`${styles.btn}${canJump ? ` ${styles.btnClickable}` : ''}`}
              >
                <div
                  className={`${styles.bubble}${isDone ? ` ${styles.bubbleDone}` : isActive ? ` ${styles.bubbleActive}` : ''}`}
                >
                  {isDone ? <SSRIcon paths={checkmarkPath} /> : stepNum}
                </div>

                <div className={styles.labelText}>
                  <div
                    className={`${styles.labelMain}${isActive || isDone ? ` ${styles.labelMainHighlighted}` : ''}`}
                  >
                    {step.label}
                  </div>
                  {subLabel && <div className={styles.labelSub}>{subLabel}</div>}
                </div>
              </button>

              {i < STEPS.length - 1 && (
                <div
                  className={`${styles.connector}${current > stepNum ? ` ${styles.connectorDone}` : ''}`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
